import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from '../../forum/app';
import { throttle } from 'throttle-debounce';
import anchorScroll from '../../common/utils/anchorScroll';
export default class PostStreamState {
  constructor(discussion, includedPosts) {
    if (includedPosts === void 0) {
      includedPosts = [];
    }
    /**
     * The discussion to display the post stream for.
     */
    _defineProperty(this, "discussion", void 0);
    /**
     * Whether or not the infinite-scrolling auto-load functionality is
     * disabled.
     */
    _defineProperty(this, "paused", false);
    _defineProperty(this, "loadPageTimeouts", {});
    _defineProperty(this, "pagesLoading", 0);
    _defineProperty(this, "index", 0);
    _defineProperty(this, "number", 1);
    /**
     * The number of posts that are currently visible in the viewport.
     */
    _defineProperty(this, "visible", 1);
    _defineProperty(this, "visibleStart", 0);
    _defineProperty(this, "visibleEnd", 0);
    _defineProperty(this, "animateScroll", false);
    _defineProperty(this, "needsScroll", false);
    _defineProperty(this, "targetPost", null);
    /**
     * The description to render on the scrubber.
     */
    _defineProperty(this, "description", '');
    /**
     * When the page is scrolled, goToIndex is called, or the page is loaded,
     * various listeners result in the scrubber being updated with a new
     * position and values. However, if goToNumber is called, the scrubber
     * will not be updated. Accordingly, we add logic to the scrubber's
     * onupdate to update itself, but only when needed, as indicated by this
     * property.
     *
     */
    _defineProperty(this, "forceUpdateScrubber", false);
    _defineProperty(this, "loadPromise", null);
    _defineProperty(this, "loadNext", void 0);
    _defineProperty(this, "loadPrevious", void 0);
    this.discussion = discussion;
    this.loadNext = throttle(300, this._loadNext);
    this.loadPrevious = throttle(300, this._loadPrevious);
    this.show(includedPosts);
  }

  /**
   * Update the stream so that it loads and includes the latest posts in the
   * discussion, if the end is being viewed.
   *
   * Note: viewingEnd() tolerates a drift of at most one post, so this is only
   * suitable when the store has gained a single new post since the window was
   * last synced (the live-event case). When several posts land in the store
   * at once — e.g. they were missed while a realtime connection was down —
   * capture viewingEnd() BEFORE pushing the new payloads and call syncEnd()
   * directly instead.
   */
  update() {
    if (!this.viewingEnd()) return Promise.resolve();
    return this.syncEnd();
  }

  /**
   * Load through to the latest post in the discussion, expanding the visible
   * window to the end of the stream. Unlike update(), this places no bound on
   * how far the window has fallen behind — the caller is responsible for
   * ensuring the end was being viewed before the posts being caught up on
   * entered the store.
   */
  syncEnd() {
    this.visibleEnd = this.count();
    return this.loadRange(this.visibleStart, this.visibleEnd);
  }

  /**
   * Load and scroll up to the first post in the discussion.
   */
  goToFirst() {
    return this.goToIndex(0);
  }

  /**
   * Load and scroll down to the last post in the discussion.
   */
  goToLast() {
    return this.goToIndex(this.count() - 1, true);
  }

  /**
   * Load and scroll to a post with a certain number.
   *
   * @param number The post number to go to. If 'reply', go to the last post and scroll the reply preview into view.
   */
  goToNumber(number, noAnimation) {
    if (noAnimation === void 0) {
      noAnimation = false;
    }
    // If we want to go to the reply preview, then we will go to the end of the
    // discussion and then scroll to the very bottom of the page.
    if (number === 'reply') {
      const resultPromise = this.goToLast();
      this.targetPost = _objectSpread(_objectSpread({}, this.targetPost), {}, {
        reply: true
      });
      return resultPromise;
    }
    this.paused = true;
    this.loadPromise = this.loadNearNumber(number);
    this.needsScroll = true;
    this.targetPost = {
      number
    };
    this.animateScroll = !noAnimation;
    this.number = number;

    // In this case, the redraw is only called after the response has been loaded
    // because we need to know the indices of the post range before we can
    // start scrolling to items. Calling redraw early causes issues.
    // Since this is only used for external navigation to the post stream, the delay
    // before the stream is moved is not an issue.
    return this.loadPromise.then(() => m.redraw());
  }

  /**
   * Load and scroll to a certain index within the discussion.
   */
  goToIndex(index, noAnimation) {
    if (noAnimation === void 0) {
      noAnimation = false;
    }
    this.paused = true;
    this.loadPromise = this.loadNearIndex(index);
    this.needsScroll = true;
    this.targetPost = {
      index
    };
    this.animateScroll = !noAnimation;
    this.index = index;
    m.redraw();
    return this.loadPromise;
  }

  /**
   * Clear the stream and load posts near a certain number. Returns a promise.
   * If the post with the given number is already loaded, the promise will be
   * resolved immediately.
   */
  loadNearNumber(number) {
    if (this.posts().some(post => post && Number(post.number()) === Number(number))) {
      return Promise.resolve();
    }
    this.reset();
    return app.store.find('posts', {
      filter: {
        discussion: this.discussion.id()
      },
      page: {
        near: number
      }
    }).then(this.show.bind(this));
  }

  /**
   * Clear the stream and load posts near a certain index. A page of posts
   * surrounding the given index will be loaded. Returns a promise. If the given
   * index is already loaded, the promise will be resolved immediately.
   */
  loadNearIndex(index) {
    if (index >= this.visibleStart && index < this.visibleEnd) {
      return Promise.resolve();
    }
    const start = this.sanitizeIndex(index - PostStreamState.loadCount / 2);
    const end = start + PostStreamState.loadCount;
    this.reset(start, end);
    return this.loadRange(start, end).then(this.show.bind(this));
  }

  /**
   * Load the next page of posts.
   */
  _loadNext() {
    const start = this.visibleEnd;
    const end = this.visibleEnd = this.sanitizeIndex(this.visibleEnd + PostStreamState.loadCount);

    // Unload the posts which are two pages back from the page we're currently
    // loading.
    const twoPagesAway = start - PostStreamState.loadCount * 2;
    if (twoPagesAway > this.visibleStart && twoPagesAway >= 0) {
      this.visibleStart = twoPagesAway + PostStreamState.loadCount + 1;
      if (this.loadPageTimeouts[twoPagesAway]) {
        clearTimeout(this.loadPageTimeouts[twoPagesAway]);
        delete this.loadPageTimeouts[twoPagesAway];
        this.pagesLoading--;
      }
    }
    this.loadPage(start, end);
  }

  /**
   * Load the previous page of posts.
   */
  _loadPrevious() {
    const end = this.visibleStart;
    const start = this.visibleStart = this.sanitizeIndex(this.visibleStart - PostStreamState.loadCount);

    // Unload the posts which are two pages back from the page we're currently
    // loading.
    const twoPagesAway = start + PostStreamState.loadCount * 2;
    if (twoPagesAway < this.visibleEnd && twoPagesAway <= this.count()) {
      this.visibleEnd = twoPagesAway;
      if (this.loadPageTimeouts[twoPagesAway]) {
        clearTimeout(this.loadPageTimeouts[twoPagesAway]);
        delete this.loadPageTimeouts[twoPagesAway];
        this.pagesLoading--;
      }
    }
    this.loadPage(start, end, true);
  }

  /**
   * Load a page of posts into the stream and redraw.
   */
  loadPage(start, end, backwards) {
    if (backwards === void 0) {
      backwards = false;
    }
    this.pagesLoading++;
    const redraw = () => {
      if (start < this.visibleStart || end > this.visibleEnd) return;
      const anchorIndex = backwards ? this.visibleEnd - 1 : this.visibleStart;
      anchorScroll(".PostStream-item[data-index=\"".concat(anchorIndex, "\"]"), m.redraw.sync);
    };
    redraw();
    this.loadPageTimeouts[start] = setTimeout(() => {
      this.loadRange(start, end).then(() => {
        redraw();
        this.pagesLoading--;
      });
      delete this.loadPageTimeouts[start];
    }, this.pagesLoading - 1 ? 1000 : 0);
  }

  /**
   * Load and inject the specified range of posts into the stream, without
   * clearing it.
   */
  loadRange(start, end) {
    const loadIds = [];
    const loaded = [];
    this.discussion.postIds().slice(start, end).forEach(id => {
      const post = app.store.getById('posts', id);
      if (post && post.discussion() && typeof post.canEdit() !== 'undefined') {
        loaded.push(post);
      } else {
        loadIds.push(id);
      }
    });
    if (loadIds.length) {
      return app.store.find('posts', loadIds).then(newPosts => {
        return loaded.concat(newPosts).sort((a, b) => a.number() - b.number());
      });
    }
    return Promise.resolve(loaded);
  }

  /**
   * Set up the stream with the given array of posts.
   */
  show(posts) {
    var _posts$0$id;
    this.visibleStart = posts.length ? this.discussion.postIds().indexOf((_posts$0$id = posts[0].id()) != null ? _posts$0$id : '0') : 0;
    this.visibleEnd = this.sanitizeIndex(this.visibleStart + posts.length);
  }

  /**
   * Reset the stream so that a specific range of posts is displayed. If a range
   * is not specified, the first page of posts will be displayed.
   */
  reset(start, end) {
    this.visibleStart = start || 0;
    this.visibleEnd = this.sanitizeIndex(end || PostStreamState.loadCount);
  }

  /**
   * Get the visible page of posts.
   */
  posts() {
    return this.discussion.postIds().slice(this.visibleStart, this.visibleEnd).map(id => {
      const post = app.store.getById('posts', id);
      return post && post.discussion() && typeof post.canEdit() !== 'undefined' ? post : null;
    });
  }

  /**
   * Get the total number of posts in the discussion.
   */
  count() {
    return this.discussion.postIds().length;
  }

  /**
   * Check whether or not the scrubber should be disabled, i.e. if all of the
   * posts are visible in the viewport.
   */
  disabled() {
    return this.visible >= this.count();
  }

  /**
   * Are we currently viewing the end of the discussion?
   */
  viewingEnd() {
    // In some cases, such as if we've stickied a post, an event post
    // may have been added / removed. This means that `this.visibleEnd`
    // and`this.count()` will be out of sync by 1 post, but we are still
    // "viewing the end" of the post stream, so we should still reload
    // all posts up until the last one.
    return Math.abs(this.count() - this.visibleEnd) <= 1;
  }

  /**
   * Make sure that the given index is not outside of the possible range of
   * indexes in the discussion.
   */
  sanitizeIndex(index) {
    return Math.max(0, Math.min(this.count(), Math.floor(index)));
  }
}
/**
 * The number of posts to load per page.
 */
_defineProperty(PostStreamState, "loadCount", 20);
flarum.reg.add('core', 'forum/states/PostStreamState', PostStreamState);